import PartInputField from '../../parts/PartInputField';
import PartRadioDotted from '../../parts/PartRadioDotted';
import ArrowRightLong from '../../icons/IconArrowRightlong';
import InvalidToastMessage from '../../../domain/notification/notificationTemplate/InvalidToastMessage';
import SuccessToastTemplate from '../../../domain/notification/notificationTemplate/SuccessToastTemplate';
import Collection from '../../../domain/valueObject/Collection';
import NotificationController from '../../../domain/notification/NotificationController';
import { useForm } from 'react-hook-form';
import { LoanTermFormProps } from './LoanTermTab';
import { yupResolver } from '@hookform/resolvers/yup';
import { loanTerm4ValidationSchema } from '../../../utils/validationSchemas';
import { LoanTermService } from '../../../domain/loanTerm/LoanTermService';
import { LoanTermFactory } from '../../../domain/loanTerm/LoanTermFactory';
import { LoanTermPurpose } from '../../../domain/loanTerm/valueObject/LoanTermLoanPurpose';
import { useEffect } from 'react';

/*
For following types loan types:
- No Doc - DSCR
*/

export const LoanTermFormType4 = ({ nextStep, loan }: LoanTermFormProps) => {
    const {
        clearErrors,
        watch,
        handleSubmit,
        control,
        formState: { errors }
    } = useForm<any>({
        resolver: yupResolver(loanTerm4ValidationSchema),
        mode: 'onBlur'
    });

    const onSubmit = async (formData: any) => {
        try {
            await new LoanTermService().create(
                LoanTermFactory.makeFromForm(
                    new Collection({
                        ...formData,
                        loan_id: loan.id,
                        loan_type: loan.loanType
                    })
                )
            );
            NotificationController.notify(
                new SuccessToastTemplate('Loan terms updated')
            );
            nextStep();
        } catch (error) {
            NotificationController.notify(
                new InvalidToastMessage('Error updating loan terms')
            );
        }
    };

    const loanAmount = watch('loan_terms-total_loan_amount');

    useEffect(() => {
        clearErrors('broker_fee_percentage');
        clearErrors('origination_fee_percentage');
    }, [loanAmount]);

    return (
        <div className="loan-terms__tab-content">
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="loan-terms__tab-form">
                    <div className="loan-terms__tab-content-box">
                        <div className="loan-terms__tab-content-left">
                            <h2 className="loan-terms__tab-content-left-title">
                                Loan terms
                            </h2>
                            <p className="loan-terms__tab-content-left-text"></p>
                        </div>
                        <div className="loan-terms__tab-content-right">
                            <div className="loan-terms__tab-content-form-box">
                                <PartRadioDotted
                                    label="Loan Purpose"
                                    name="loan_terms-loan_purpose"
                                    control={control}
                                    errors={errors}
                                    isRequired
                                    options={[
                                        {
                                            label: LoanTermPurpose.PURCHASE_NAME,
                                            value: LoanTermPurpose.PURCHASE
                                        },
                                        {
                                            label: LoanTermPurpose.RATE_TERM_REFINANCE_NAME,
                                            value: LoanTermPurpose.RATE_TERM_REFINANCE
                                        },
                                        {
                                            label: LoanTermPurpose.CASH_OUT_REFINANCE_NAME,
                                            value: LoanTermPurpose.CASH_OUT_REFINANCE
                                        }
                                    ]}
                                />
                                <PartInputField
                                    label="Loan Amount"
                                    name="loan_terms-total_loan_amount"
                                    control={control}
                                    errors={errors}
                                    isRequired
                                    isDollarAmount
                                />
                                <PartInputField
                                    label="Loan to Value"
                                    name="loan_terms-loan_to_value"
                                    control={control}
                                    errors={errors}
                                    isPercentageAmount
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="loan-terms__tab-form">
                    <div className="loan-terms__tab-content-box">
                        <div className="loan-terms__tab-content-left">
                            <h2 className="loan-terms__tab-content-left-title">
                                Pricing
                            </h2>
                            <p className="loan-terms__tab-content-left-text"></p>
                        </div>
                        <div className="loan-terms__tab-content-right">
                            <div className="loan-terms__tab-content-form-box">
                                <PartInputField
                                    name="pricing-interest_rate"
                                    label="Interest Rate"
                                    control={control}
                                    errors={errors}
                                    isPercentageAmount
                                    isRequired
                                />
                                <PartRadioDotted
                                    name="pricing-rate_type"
                                    control={control}
                                    errors={errors}
                                    label="Rate type"
                                    options={[
                                        {
                                            label: '5/6 ARM',
                                            value: 1
                                        },
                                        {
                                            label: '7/6 ARM',
                                            value: 2
                                        },
                                        {
                                            label: '10/6 ARM',
                                            value: 3
                                        },
                                        {
                                            label: '30 yr FRM',
                                            value: 4
                                        }
                                    ]}
                                />
                                <PartRadioDotted
                                    label="Amortization Type"
                                    name="pricing-amortization_type"
                                    control={control}
                                    errors={errors}
                                    options={[
                                        {
                                            label: 'Fully Amortizing',
                                            value: 1
                                        },
                                        {
                                            label: 'Partial Interest Only',
                                            value: 2
                                        }
                                    ]}
                                />
                                <PartRadioDotted
                                    label="Pre-Payment Penalty"
                                    name="pricing-pre_payment_penalty"
                                    control={control}
                                    errors={errors}
                                    options={[
                                        {
                                            label: '3 year (3-2-1)',
                                            value: 1
                                        },
                                        {
                                            label: '4 year (2-3-2-1)',
                                            value: 2
                                        },
                                        {
                                            label: '5 year (5-4-3-2-1)',
                                            value: 3
                                        },
                                        {
                                            label: 'Yield Maintenace',
                                            value: 4
                                        },
                                        {
                                            label: '3 year (3-0-0)',
                                            value: 5
                                        },
                                        {
                                            label: '1 year (1-0-0)',
                                            value: 6
                                        }
                                    ]}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="loan-terms__tab-form">
                    <div className="loan-terms__tab-content-box">
                        <div className="loan-terms__tab-content-left">
                            <h2 className="loan-terms__tab-content-left-title">
                                Fees and Escrows
                            </h2>
                            <p className="loan-terms__tab-content-left-text"></p>
                        </div>
                        <div className="loan-terms__tab-content-right">
                            <div className="loan-terms__tab-content-form-box">
                                <PartInputField
                                    label="Origination Fee"
                                    name="origination_fee_percentage"
                                    control={control}
                                    errors={errors}
                                    isRequired
                                    isPercentageAmount
                                />
                                <PartInputField
                                    label="Broker Fee"
                                    name="broker_fee_percentage"
                                    control={control}
                                    errors={errors}
                                    isRequired
                                    isPercentageAmount
                                />
                                <PartInputField
                                    name="underwriting_fee"
                                    label="Underwriting Fee"
                                    control={control}
                                    errors={errors}
                                    isDollarAmount
                                />
                                <PartInputField
                                    name="processing_fee"
                                    label="Processing Fee"
                                    control={control}
                                    errors={errors}
                                    isDollarAmount
                                />
                                <PartInputField
                                    name="legal_fee"
                                    label="Legal Fee"
                                    control={control}
                                    errors={errors}
                                    isDollarAmount
                                />
                                <PartRadioDotted
                                    label="PITI Reserve"
                                    name="fees_and_escrows-piti_reserve"
                                    control={control}
                                    errors={errors}
                                    isRequired
                                    options={[
                                        {
                                            label: 'None',
                                            value: 1
                                        },
                                        {
                                            label: '1 month',
                                            value: 2
                                        },
                                        {
                                            label: '3 months',
                                            value: 3
                                        },
                                        {
                                            label: '6 months',
                                            value: 4
                                        }
                                    ]}
                                />
                                <PartInputField
                                    label="Insurance Escrow"
                                    name="fees_and_escrows-insurance_escrow_amount"
                                    control={control}
                                    errors={errors}
                                    isDollarAmount
                                />
                                <PartInputField
                                    label="Tax Escrow"
                                    name="fees_and_escrows-tax_escrow_amount"
                                    control={control}
                                    errors={errors}
                                    isDollarAmount
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="loan-terms__tab-form">
                    <div className="loan-terms__tab-content-box">
                        <div className="loan-terms__tab-content-left">
                            <h2 className="loan-terms__tab-content-left-title">
                                Closing Conditions
                            </h2>
                            <p className="loan-terms__tab-content-left-text"></p>
                        </div>
                        <div className="loan-terms__tab-content-right">
                            <div className="loan-terms__tab-content-form-box">
                                <PartInputField
                                    label="LTV"
                                    name="cc-ltv"
                                    control={control}
                                    errors={errors}
                                    isRequired
                                    isPercentageAmount
                                />
                                <PartInputField
                                    label="Min Credit Score"
                                    name="cc-min_credit_score"
                                    control={control}
                                    errors={errors}
                                    isRequired
                                    isNumberFormat
                                />
                                <PartInputField
                                    label="Verified Properties"
                                    name="cc-track_record"
                                    control={control}
                                    errors={errors}
                                    isRequired
                                    isNumberFormat
                                />
                                <PartInputField
                                    label="Liquidity"
                                    name="cc-liquidity"
                                    control={control}
                                    errors={errors}
                                    isRequired
                                    isDollarAmount
                                />
                                <PartInputField
                                    label="Net worth"
                                    name="cc-net_worth"
                                    control={control}
                                    errors={errors}
                                    isRequired
                                    isDollarAmount
                                />
                                <PartInputField
                                    label="DSCR"
                                    name="cc-dscr"
                                    control={control}
                                    errors={errors}
                                    isPercentageAmount
                                />
                            </div>
                            <div className="loan-terms__tab-submit-wrapp">
                                <div className="loan-terms__button-submit-btn">
                                    <input
                                        type="submit"
                                        className="loan-terms__button-submit"
                                        value={'Save and Submit'}
                                    />
                                    <ArrowRightLong />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};
